// src/utils/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/Button.tsx
import { forwardRef } from "react";
import { Loader2 } from "lucide-react";
import { jsx, jsxs } from "react/jsx-runtime";
var VARIANTS = {
  primary: "bg-brand-600 text-white shadow-sm hover:bg-brand-700 focus-visible:outline-brand-600 disabled:bg-brand-300",
  secondary: "bg-slate-100 text-slate-900 hover:bg-slate-200 focus-visible:outline-slate-400 disabled:text-slate-400",
  outline: "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus-visible:outline-slate-400",
  ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-slate-400",
  danger: "bg-rose-600 text-white shadow-sm hover:bg-rose-700 focus-visible:outline-rose-600",
  success: "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:outline-emerald-600"
};
var SIZES = {
  sm: "h-8 px-3 text-xs gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2"
};
var Button = forwardRef(function Button2({ className, variant = "primary", size = "md", isLoading, leftIcon, rightIcon, fullWidth, children, disabled, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      disabled: disabled || isLoading,
      className: cn(
        "inline-flex items-center justify-center rounded-lg font-medium transition-colors",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-60",
        VARIANTS[variant],
        SIZES[size],
        fullWidth && "w-full",
        className
      ),
      ...props,
      children: [
        isLoading ? /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin", "aria-hidden": true }) : leftIcon,
        children,
        !isLoading && rightIcon
      ]
    }
  );
});

// src/components/Card.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function Card({ className, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: cn("rounded-xl border border-slate-200 bg-white shadow-sm", className),
      ...props
    }
  );
}
function CardHeader({ title, description, action, className, ...props }) {
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      className: cn("flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4", className),
      ...props,
      children: [
        /* @__PURE__ */ jsxs2("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx2("h2", { className: "text-sm font-semibold text-slate-900", children: title }),
          description && /* @__PURE__ */ jsx2("p", { className: "mt-0.5 text-sm text-slate-500", children: description })
        ] }),
        action && /* @__PURE__ */ jsx2("div", { className: "shrink-0", children: action })
      ]
    }
  );
}
function CardBody({ className, ...props }) {
  return /* @__PURE__ */ jsx2("div", { className: cn("px-5 py-4", className), ...props });
}
function CardFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx2("div", { className: cn("border-t border-slate-100 px-5 py-3", className), ...props });
}

// src/components/Badge.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var TONES = {
  neutral: "bg-slate-100 text-slate-700 ring-slate-200",
  brand: "bg-brand-50 text-brand-700 ring-brand-200",
  success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  warning: "bg-amber-50 text-amber-700 ring-amber-200",
  danger: "bg-rose-50 text-rose-700 ring-rose-200",
  info: "bg-sky-50 text-sky-700 ring-sky-200"
};
var DOTS = {
  neutral: "bg-slate-400",
  brand: "bg-brand-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-rose-500",
  info: "bg-sky-500"
};
function Badge({ tone = "neutral", dot, className, children, ...props }) {
  return /* @__PURE__ */ jsxs3(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        TONES[tone],
        className
      ),
      ...props,
      children: [
        dot && /* @__PURE__ */ jsx3("span", { className: cn("h-1.5 w-1.5 rounded-full", DOTS[tone]), "aria-hidden": true }),
        children
      ]
    }
  );
}

// src/components/Avatar.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var SIZES2 = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-16 w-16 text-xl"
};
var PALETTE = [
  "bg-brand-100 text-brand-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-violet-100 text-violet-700",
  "bg-sky-100 text-sky-700"
];
function initialsOf(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}
function paletteFor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = hash * 31 + name.charCodeAt(i) >>> 0;
  return PALETTE[hash % PALETTE.length];
}
function Avatar({ name, src, size = "md", className, status }) {
  return /* @__PURE__ */ jsxs4("span", { className: cn("relative inline-flex shrink-0", className), children: [
    src ? /* @__PURE__ */ jsx4(
      "img",
      {
        src,
        alt: name,
        className: cn("rounded-full object-cover ring-1 ring-slate-200", SIZES2[size])
      }
    ) : /* @__PURE__ */ jsx4(
      "span",
      {
        "aria-hidden": true,
        title: name,
        className: cn(
          "inline-flex items-center justify-center rounded-full font-semibold ring-1 ring-inset ring-black/5",
          SIZES2[size],
          paletteFor(name)
        ),
        children: initialsOf(name)
      }
    ),
    status && /* @__PURE__ */ jsx4(
      "span",
      {
        className: cn(
          "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white",
          status === "online" ? "bg-emerald-500" : "bg-slate-300"
        ),
        "aria-label": status
      }
    )
  ] });
}

// src/components/Input.tsx
import { forwardRef as forwardRef2, useId } from "react";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var Input = forwardRef2(function Input2({ label, error, hint, leftIcon, rightSlot, className, id, ...props }, ref) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : void 0;
  return /* @__PURE__ */ jsxs5("div", { className: "w-full", children: [
    label && /* @__PURE__ */ jsx5("label", { htmlFor: inputId, className: "mb-1.5 block text-sm font-medium text-slate-700", children: label }),
    /* @__PURE__ */ jsxs5("div", { className: "relative", children: [
      leftIcon && /* @__PURE__ */ jsx5("span", { className: "pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400", children: leftIcon }),
      /* @__PURE__ */ jsx5(
        "input",
        {
          ref,
          id: inputId,
          "aria-invalid": error ? true : void 0,
          "aria-describedby": describedBy,
          className: cn(
            "block w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm",
            "placeholder:text-slate-400",
            "focus:outline-none focus:ring-2 focus:ring-offset-0",
            "disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500",
            error ? "border-rose-300 focus:border-rose-500 focus:ring-rose-200" : "border-slate-300 focus:border-brand-500 focus:ring-brand-200",
            leftIcon && "pl-10",
            rightSlot && "pr-10",
            className
          ),
          ...props
        }
      ),
      rightSlot && /* @__PURE__ */ jsx5("span", { className: "absolute inset-y-0 right-0 flex items-center pr-2", children: rightSlot })
    ] }),
    error ? /* @__PURE__ */ jsx5("p", { id: `${inputId}-error`, role: "alert", className: "mt-1.5 text-sm text-rose-600", children: error }) : hint ? /* @__PURE__ */ jsx5("p", { id: `${inputId}-hint`, className: "mt-1.5 text-sm text-slate-500", children: hint }) : null
  ] });
});

// src/components/Select.tsx
import { forwardRef as forwardRef3, useId as useId2 } from "react";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var Select = forwardRef3(function Select2({ label, error, hint, className, id, children, ...props }, ref) {
  const generatedId = useId2();
  const fieldId = id ?? generatedId;
  return /* @__PURE__ */ jsxs6("div", { className: "w-full", children: [
    label && /* @__PURE__ */ jsx6("label", { htmlFor: fieldId, className: "mb-1.5 block text-sm font-medium text-slate-700", children: label }),
    /* @__PURE__ */ jsx6(
      "select",
      {
        ref,
        id: fieldId,
        "aria-invalid": error ? true : void 0,
        className: cn(
          "block w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm",
          "focus:outline-none focus:ring-2 disabled:bg-slate-50",
          error ? "border-rose-300 focus:border-rose-500 focus:ring-rose-200" : "border-slate-300 focus:border-brand-500 focus:ring-brand-200",
          className
        ),
        ...props,
        children
      }
    ),
    error ? /* @__PURE__ */ jsx6("p", { role: "alert", className: "mt-1.5 text-sm text-rose-600", children: error }) : hint ? /* @__PURE__ */ jsx6("p", { className: "mt-1.5 text-sm text-slate-500", children: hint }) : null
  ] });
});

// src/components/Modal.tsx
import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var SIZES3 = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl"
};
function Modal({ open, onClose, title, description, children, footer, size = "md" }) {
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    panelRef.current?.querySelector("input, select, textarea, button")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);
  if (!open) return null;
  return /* @__PURE__ */ jsxs7("div", { className: "fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center", children: [
    /* @__PURE__ */ jsx7("div", { className: "fixed inset-0 bg-slate-900/50 backdrop-blur-sm", onClick: onClose, "aria-hidden": true }),
    /* @__PURE__ */ jsxs7(
      "div",
      {
        ref: panelRef,
        role: "dialog",
        "aria-modal": "true",
        "aria-label": title,
        className: cn(
          "relative w-full rounded-xl bg-white shadow-xl animate-fade-in",
          SIZES3[size]
        ),
        children: [
          /* @__PURE__ */ jsxs7("div", { className: "flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4", children: [
            /* @__PURE__ */ jsxs7("div", { className: "min-w-0", children: [
              /* @__PURE__ */ jsx7("h2", { className: "text-base font-semibold text-slate-900", children: title }),
              description && /* @__PURE__ */ jsx7("p", { className: "mt-0.5 text-sm text-slate-500", children: description })
            ] }),
            /* @__PURE__ */ jsx7(
              "button",
              {
                type: "button",
                onClick: onClose,
                "aria-label": "Close",
                className: "rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600",
                children: /* @__PURE__ */ jsx7(X, { className: "h-4 w-4", "aria-hidden": true })
              }
            )
          ] }),
          /* @__PURE__ */ jsx7("div", { className: "max-h-[70vh] overflow-y-auto px-5 py-4", children }),
          footer && /* @__PURE__ */ jsx7("div", { className: "flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3", children: footer })
        ]
      }
    )
  ] });
}

// src/components/feedback.tsx
import { AlertCircle, Inbox, Loader2 as Loader22 } from "lucide-react";
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
function Spinner({ className }) {
  return /* @__PURE__ */ jsx8(Loader22, { className: cn("h-5 w-5 animate-spin text-slate-400", className), "aria-hidden": true });
}
function LoadingState({ label = "Loading\u2026", className }) {
  return /* @__PURE__ */ jsxs8("div", { className: cn("flex items-center justify-center gap-3 py-12 text-sm text-slate-500", className), role: "status", children: [
    /* @__PURE__ */ jsx8(Spinner, {}),
    label
  ] });
}
function EmptyState({ title, description, icon, action, className }) {
  return /* @__PURE__ */ jsxs8("div", { className: cn("flex flex-col items-center justify-center px-6 py-12 text-center", className), children: [
    /* @__PURE__ */ jsx8("div", { className: "mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400", children: icon ?? /* @__PURE__ */ jsx8(Inbox, { className: "h-5 w-5", "aria-hidden": true }) }),
    /* @__PURE__ */ jsx8("p", { className: "text-sm font-medium text-slate-900", children: title }),
    description && /* @__PURE__ */ jsx8("p", { className: "mt-1 max-w-sm text-sm text-slate-500", children: description }),
    action && /* @__PURE__ */ jsx8("div", { className: "mt-4", children: action })
  ] });
}
function ErrorState({
  title = "Something went wrong",
  description,
  action,
  className
}) {
  return /* @__PURE__ */ jsxs8("div", { className: cn("flex flex-col items-center justify-center px-6 py-12 text-center", className), role: "alert", children: [
    /* @__PURE__ */ jsx8("div", { className: "mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-rose-50 text-rose-500", children: /* @__PURE__ */ jsx8(AlertCircle, { className: "h-5 w-5", "aria-hidden": true }) }),
    /* @__PURE__ */ jsx8("p", { className: "text-sm font-medium text-slate-900", children: title }),
    description && /* @__PURE__ */ jsx8("p", { className: "mt-1 max-w-sm text-sm text-slate-500", children: description }),
    action && /* @__PURE__ */ jsx8("div", { className: "mt-4", children: action })
  ] });
}
function Alert({
  tone = "info",
  title,
  children,
  className
}) {
  const tones = {
    info: "bg-sky-50 text-sky-800 border-sky-200",
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-rose-50 text-rose-800 border-rose-200"
  };
  return /* @__PURE__ */ jsxs8("div", { className: cn("rounded-lg border px-4 py-3 text-sm", tones[tone], className), role: "alert", children: [
    title && /* @__PURE__ */ jsx8("p", { className: "font-medium", children: title }),
    children && /* @__PURE__ */ jsx8("div", { className: cn(title && "mt-1"), children })
  ] });
}

// src/components/StatCard.tsx
import { jsx as jsx9, jsxs as jsxs9 } from "react/jsx-runtime";
var TONES2 = {
  neutral: "bg-slate-100 text-slate-600",
  brand: "bg-brand-50 text-brand-600",
  success: "bg-emerald-50 text-emerald-600",
  warning: "bg-amber-50 text-amber-600",
  danger: "bg-rose-50 text-rose-600"
};
function StatCard({ label, value, hint, icon, tone = "neutral", className }) {
  return /* @__PURE__ */ jsx9("div", { className: cn("rounded-xl border border-slate-200 bg-white p-4 shadow-sm", className), children: /* @__PURE__ */ jsxs9("div", { className: "flex items-start justify-between gap-3", children: [
    /* @__PURE__ */ jsxs9("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx9("p", { className: "truncate text-xs font-medium uppercase tracking-wide text-slate-500", children: label }),
      /* @__PURE__ */ jsx9("p", { className: "mt-1.5 text-2xl font-semibold tabular-nums text-slate-900", children: value }),
      hint && /* @__PURE__ */ jsx9("p", { className: "mt-0.5 truncate text-xs text-slate-500", children: hint })
    ] }),
    icon && /* @__PURE__ */ jsx9("span", { className: cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", TONES2[tone]), children: icon })
  ] }) });
}

// src/components/ProgressBar.tsx
import { jsx as jsx10, jsxs as jsxs10 } from "react/jsx-runtime";
function autoTone(value) {
  if (value >= 70) return "success";
  if (value >= 50) return "warning";
  return "danger";
}
var FILL = {
  brand: "bg-brand-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-rose-500"
};
function ProgressBar({
  value,
  label,
  showValue = true,
  size = "md",
  tone = "auto",
  className
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  const resolved = tone === "auto" ? autoTone(clamped) : tone;
  return /* @__PURE__ */ jsxs10("div", { className, children: [
    (label || showValue) && /* @__PURE__ */ jsxs10("div", { className: "mb-1.5 flex items-baseline justify-between gap-2", children: [
      label && /* @__PURE__ */ jsx10("span", { className: "truncate text-sm font-medium text-slate-700", children: label }),
      showValue && /* @__PURE__ */ jsxs10("span", { className: "text-sm font-semibold tabular-nums text-slate-900", children: [
        clamped,
        "%"
      ] })
    ] }),
    /* @__PURE__ */ jsx10(
      "div",
      {
        role: "progressbar",
        "aria-valuenow": clamped,
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-label": label,
        className: cn("w-full overflow-hidden rounded-full bg-slate-100", size === "sm" ? "h-1.5" : "h-2"),
        children: /* @__PURE__ */ jsx10(
          "div",
          {
            className: cn("h-full rounded-full transition-all duration-500", FILL[resolved]),
            style: { width: `${clamped}%` }
          }
        )
      }
    )
  ] });
}

// src/components/misc.tsx
import { forwardRef as forwardRef4, useId as useId3 } from "react";
import { jsx as jsx11, jsxs as jsxs11 } from "react/jsx-runtime";
function PageHeader({
  title,
  description,
  action,
  className
}) {
  return /* @__PURE__ */ jsxs11("div", { className: cn("mb-6 flex flex-wrap items-start justify-between gap-4", className), children: [
    /* @__PURE__ */ jsxs11("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx11("h1", { className: "text-2xl font-semibold tracking-tight text-slate-900", children: title }),
      description && /* @__PURE__ */ jsx11("p", { className: "mt-1 text-sm text-slate-500", children: description })
    ] }),
    action && /* @__PURE__ */ jsx11("div", { className: "shrink-0", children: action })
  ] });
}
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx11("div", { className: cn("animate-pulse rounded-md bg-slate-200", className), ...props });
}
var Textarea = forwardRef4(function Textarea2({ label, error, hint, className, id, ...props }, ref) {
  const generatedId = useId3();
  const fieldId = id ?? generatedId;
  return /* @__PURE__ */ jsxs11("div", { className: "w-full", children: [
    label && /* @__PURE__ */ jsx11("label", { htmlFor: fieldId, className: "mb-1.5 block text-sm font-medium text-slate-700", children: label }),
    /* @__PURE__ */ jsx11(
      "textarea",
      {
        ref,
        id: fieldId,
        "aria-invalid": error ? true : void 0,
        className: cn(
          "block w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm",
          "placeholder:text-slate-400 focus:outline-none focus:ring-2",
          error ? "border-rose-300 focus:border-rose-500 focus:ring-rose-200" : "border-slate-300 focus:border-brand-500 focus:ring-brand-200",
          className
        ),
        ...props
      }
    ),
    error ? /* @__PURE__ */ jsx11("p", { role: "alert", className: "mt-1.5 text-sm text-rose-600", children: error }) : hint ? /* @__PURE__ */ jsx11("p", { className: "mt-1.5 text-sm text-slate-500", children: hint }) : null
  ] });
});
function Divider({ label, className }) {
  if (!label) return /* @__PURE__ */ jsx11("hr", { className: cn("border-slate-200", className) });
  return /* @__PURE__ */ jsxs11("div", { className: cn("relative flex items-center", className), children: [
    /* @__PURE__ */ jsx11("span", { className: "flex-grow border-t border-slate-200" }),
    /* @__PURE__ */ jsx11("span", { className: "mx-3 text-xs font-medium uppercase tracking-wide text-slate-400", children: label }),
    /* @__PURE__ */ jsx11("span", { className: "flex-grow border-t border-slate-200" })
  ] });
}
export {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  ProgressBar,
  Select,
  Skeleton,
  Spinner,
  StatCard,
  Textarea,
  cn
};
//# sourceMappingURL=index.js.map